import * as _ from 'lodash';
import { useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import type { InjectedRouter } from 'react-router';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { useDispatchOnMount, useOnUnmount } from '../../../../hooks';
import RolesModule from '../../../../redux/roles';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import Loader from '../../../generic/Loader';
import Redirect from '../../../generic/Redirect';
import { InvalidPermissionError } from '../../Errors';
import type { RolePermission } from '../../../../../shared/model-typings';
import type { CreateRoleRequest, EditRoleRequest, Role } from '../../../../../typings/api';
import type { EditingRoleState } from '../../../../redux/roles';

import AddEditRole from './AddEditRole';

type DispatchProps = {
  setEditingRole: (roleId: string | null) => void;
  setEditingPermissions: (permissions: Pick<EditingRoleState, 'permissions'>) => void;
  updateEditingRole: (updatedEditingRole: Partial<EditingRoleState>) => void;
  clearEditingRole: () => void;
  addRole: (role: CreateRoleRequest) => Promise<void>;
  updateRole: (role: EditRoleRequest) => Promise<void>;
};

type OrganizationRolesEditProps = DispatchProps & {
  role: Role;
  hasSetEditingRole: boolean;
  hasBeenEdited: boolean;
  isSaving: boolean;
  routeParams?: {
    id: string;
  };
  router: InjectedRouter;
};

//  The backend only needs the action + resourceType
const pickPermissionForSaving = (permission: RolePermission) => _.pick(permission, ['action', 'resourceType']);

//  Stored up here to make the onConfirm call a bit easier to parse.
const CONFIRM_UNSAVED_CHANGES_TITLE = 'Are you sure?';
const CONFIRM_UNSAVED_CHANGES_MESSAGE =
  'You have unsaved changes which will be lost by navigating away from this page. Are you sure you want to do this?';

/**
 * Container component for editing a role. Handles setting the `state.roles.editingRole` on mount,
 * based on the root
 */
const OrganizationRolesEdit = ({
  role,
  routeParams,
  setEditingRole,
  setEditingPermissions,
  updateEditingRole,
  router,
  clearEditingRole,
  hasSetEditingRole,
  hasBeenEdited,
  isSaving,
  addRole,
  updateRole,
}: OrganizationRolesEditProps) => {
  const { onConfirm } = useDialogContext();
  const hasPermission = useHasPermission();
  const canManageRoles = hasPermission({
    action: Action.Manage,
    resourceType: ResourceType.Role,
  });

  //  Set the active "editing" role based on the routeParams.id.
  useEffect(() => {
    if (routeParams?.id && routeParams.id !== 'clone') {
      setEditingRole(routeParams.id);
    }
  }, [routeParams?.id, setEditingRole]);

  //  "clone-mode" effectively works the same as add mode, but is pre-populated by dispatching the cloneRole action.
  const isAddMode = routeParams?.id === 'add' || routeParams?.id === 'clone';
  const { id, name, description, scope, permissions, isCustom } = role;

  //  Go back to the roles page.
  const navigateBackToRolesIndex = () => router.push('/account/settings/organization/roles');

  //  When a user confirms cancel click.
  const navigateBackAndClearRole = () => {
    navigateBackToRolesIndex();
    //  Clear out the editing role state in Redux.
    clearEditingRole();
  };

  //  Handle what happens when the user clicks "Cancel"
  const onCancelClick = () =>
    !hasBeenEdited
      ? navigateBackAndClearRole()
      : onConfirm({
        title: CONFIRM_UNSAVED_CHANGES_TITLE,
        body: CONFIRM_UNSAVED_CHANGES_MESSAGE,
        onConfirm: navigateBackAndClearRole,
      });

  const onSaveClick = () => {
    const saveRole = isAddMode ? addRole : updateRole;
    return saveRole({
      id,
      name,
      description,
      scope,
      permissions: permissions?.map(pickPermissionForSaving),
    }).then(navigateBackToRolesIndex);
  };

  if (!canManageRoles) {
    return <InvalidPermissionError />;
  }

  //  Because the "cloned" role is only ever stored in Redux,
  //  if the user happens to refresh the page when on the /clone
  //  route, we have no way to know what role they were cloning so we just redirect them.
  //  This isn't a perfect solution but is the best we can do now. The real fix
  //  would be to store the cloned role in the URL params, like `/clone/:roleId`
  //  which is out of scope for this ticket.
  if (routeParams?.id === 'clone' && role.id === null) {
    return <Redirect to="/account/settings/organization/roles" />;
  }

  return !hasSetEditingRole ? (
    <Loader />
  ) : (
    <AddEditRole
      name={name}
      description={description}
      scope={scope}
      permissions={permissions}
      updateEditingRole={updateEditingRole}
      setEditingPermissions={setEditingPermissions}
      isAddMode={isAddMode}
      isReadOnly={!isAddMode && !isCustom}
      isSaving={isSaving}
      onCancelClick={onCancelClick}
      onSaveClick={onSaveClick}
    />
  );
};

const mapStateToProps = createStructuredSelector({
  role: RolesModule.selectors.getEditingRole,
  hasBeenEdited: RolesModule.selectors.getEditingRoleHasBeenEdited,
  hasSetEditingRole: RolesModule.selectors.hasSetEditingRole,
  isSaving: RolesModule.selectors.getIsSaving,
});

const mapDispatchToProps = {
  setEditingRole: RolesModule.actions.setEditingRole,
  setEditingPermissions: RolesModule.actions.setEditingPermissions,
  updateEditingRole: RolesModule.actions.updateEditingRole,
  clearEditingRole: RolesModule.actions.clearEditingRole,
  addRole: RolesModule.actions.create,
  updateRole: RolesModule.actions.update,
};

const ConnectedOrganizationRolesEdit = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OrganizationRolesEdit);

//  This container ensures that Roles are fetched BEFORE rendering the component.
const OrganizationRolesEditContainer = (props: OrganizationRolesEditProps) => {
  const dispatch = useDispatch();
  const status = useSelector(RolesModule.selectors.getStatus);

  useDispatchOnMount(RolesModule.actions.fetchAll);

  //  Clear the editing role when the component unmounts.
  useOnUnmount(() => {
    dispatch(RolesModule.actions.clearEditingRole());
  });

  if (status.isLoading) {
    return (
      <Loader />
    );
  }

  return (
    <ConnectedOrganizationRolesEdit
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};

export default OrganizationRolesEditContainer;
